import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import LoadingPage from "@/components/LoadingPage";
import { useSegmentCallbackV2 } from "@/src/segment";
import useEnv from "@/src/useEnv";
import Button, { ButtonTheme } from "@/components/Button";
import {
  AiOutlineClose,
  AiOutlineArrowLeft,
  AiOutlineArrowRight,
  AiOutlineDiff,
  AiOutlineHistory,
  AiOutlineSave,
} from "react-icons/ai";
import {
  HiOutlineCheck,
  HiOutlineExclamation,
  HiOutlineX,
} from "react-icons/hi";
import Modal from "@/components/Modal";
import useData from "@/src/useData";
import { formatDistanceToNow } from "date-fns";
import {
  EnvData,
  PolicyError,
  PolicyTestResult,
  PolicyVersion,
  Stats,
} from "@/src/types";
import { usePolicyAutosave } from "@/src/policyAutosave";
import { useEnvironmentAction } from "@/src/useAction";
import { Policy, StaticDiffPolicy } from "@/components/Policy";
import dynamic from "next/dynamic";
import type { MonacoEditorLanguageClientWrapper } from "monaco-editor-wrapper";
import extensionConfiguration from "@/src/language-configuration.json";
import tmLanguageSyntax from "@/src/polar.tmLanguage.json";
import { useTransition } from "transition-hook";

type PolicySaveError =
  | {
      error_type: "Generic";
      message: string;
    }
  | {
      error_type: "TestsFailed";
      message: string;
      test_results: PolicyTestResult;
    }
  | {
      error_type: "Validation";
      errors: PolicyError[];
    };

class PolicySaveErrorEvent extends Event {
  readonly error: PolicySaveError;
  constructor(error: PolicySaveError) {
    super("savingFailure");
    this.error = error;
  }
}

enum SavingState {
  Default,
  Saving,
  SavingSuccessExisting,
  SavingFailure,
  SavingFailureUnexpected,
  SavingFailureForbidden,
  SavingSuccess,
  RunningTests,
  RunningTestsFailure,
  RunningTestsSuccess,
}

const versionToDate = (
  version: PolicyVersion
): {
  localeString: string;
  relative: string;
} => {
  const date = new Date(version.created_at);
  return {
    localeString: date.toLocaleString(),
    relative: formatDistanceToNow(date, { addSuffix: true }),
  };
};

// This component handles fetching policy source for a particular version.
const PolicyRevision: FunctionComponent<{
  version: PolicyVersion;
  selectedEnv: EnvData;
  onRestore: (policy: string) => void;
  onDiff: (policy: string) => void;
}> = ({ version, selectedEnv, onRestore, onDiff }) => {
  const { localeString, relative } = versionToDate(version);
  const timestamp = `Version ${version.id} - ${localeString} (${relative})`;
  const updatePolicyAction = useEnvironmentAction("update_policy");

  // TODO: useData (but not eagerly)
  const fetchPolicy = async () => {
    try {
      const resp = await fetch(
        `/web/environment/${selectedEnv.pub_id}/api/policy?id=${version.id}`
      );
      return (await resp.json()).policy;
    } catch (e) {
      const msg = `Error while fetching policy: ${e}`;
      console.error(msg);
    }
  };

  return (
    <div className="flex items-center justify-between  mb-1 group hover:bg-gray-50">
      <div className="font-mono text-sm">{timestamp}</div>
      <div className="space-x-4">
        <Button
          medium
          theme="secondary"
          icon={<AiOutlineDiff />}
          onClick={async () => {
            // TODO: error handling?
            let policy = await fetchPolicy();
            onDiff(policy.src);
          }}
        >
          Diff
        </Button>
        <Button
          medium
          theme="primary"
          disabled={!updatePolicyAction}
          icon={<AiOutlineHistory />}
          onClick={async () => {
            let policy = await fetchPolicy();
            onRestore(policy.src);
          }}
        >
          Restore
        </Button>
      </div>
    </div>
  );
};

const PolicyHistoryModal: FunctionComponent<{
  isOpen: boolean;
  onClose: () => void;
  stats: Stats;
  refreshStats: () => void;
  selectedEnv: EnvData;
  currentPolicy: string;
  setPolicy: (policy: string) => Promise<void>;
}> = ({
  isOpen,
  onClose,
  stats,
  refreshStats,
  selectedEnv,
  currentPolicy,
  setPolicy,
}) => {
  type ModalState =
    | { mode: "List" }
    | { mode: "Diff"; version: PolicyVersion; policy_to_diff: string }
    | {
        mode: "ConfirmRestore";
        version: PolicyVersion;
        policy_to_restore: string;
      };

  const DEFAULT_MODAL_STATE: ModalState = { mode: "List" };

  const [modalState, setModalState] = useState<ModalState>(DEFAULT_MODAL_STATE);
  const [pageNumber, setPageNumber] = useState<number>(0);

  const closeModal = () => {
    setModalState(DEFAULT_MODAL_STATE);
    onClose();
  };

  // split flat list into pages
  const pageSize = 10;
  const paginate = (versions: PolicyVersion[]): PolicyVersion[][] => {
    // do not paginate if the user has no policy
    if (versions.length == 0) {
      return [];
    }

    const pages = [];
    let index = 0;
    while (index < versions.length) {
      pages.push(versions.slice(index, index + pageSize));
      index += pageSize;
    }

    return pages;
  };

  const pages: PolicyVersion[][] = paginate(stats.policy.versions);
  const totalVersions = stats.policy.versions.length;
  const startIndex = totalVersions - pageNumber * pageSize;
  const paginationLabel = `${startIndex}-${Math.max(
    1,
    startIndex - pageSize + 1
  )} of ${totalVersions}`;

  switch (modalState.mode) {
    case "List":
      return pages.length === 0 ? (
        <Modal wide={true} isOpen={isOpen} onClose={closeModal}>
          <h1 className="font-bold">Policy History</h1>
          <div className="border-b my-4" />
          <h2>
            No saved policies found. After storing your first policy, you can
            use this interface to restore older versions.
          </h2>
        </Modal>
      ) : (
        <Modal wide={true} isOpen={isOpen} onClose={closeModal}>
          <h1 className="font-bold">Policy History</h1>
          <div className="border-b my-4" />
          <ul>
            {pages[pageNumber].map((version) => (
              <li key={`${version.id}`}>
                <PolicyRevision
                  version={version}
                  selectedEnv={selectedEnv}
                  onRestore={(policy: string) => {
                    setModalState({
                      mode: "ConfirmRestore",
                      version: version,
                      policy_to_restore: policy,
                    });
                  }}
                  onDiff={(policy: string) => {
                    setModalState({
                      mode: "Diff",
                      version: version,
                      policy_to_diff: policy,
                    });
                  }}
                />
              </li>
            ))}
          </ul>
          <div className="flex justify-between mt-2">
            <Button medium icon={<HiOutlineX />} onClick={closeModal}>
              Return to Rules Editor
            </Button>
            <div className="space-x-2">
              {paginationLabel}{" "}
              <Button
                medium
                disabled={pageNumber < 1}
                icon={<AiOutlineArrowLeft />}
                onClick={() => setPageNumber(pageNumber - 1)}
              />
              <Button
                medium
                disabled={pageNumber + 1 > pages.length - 1}
                icon={<AiOutlineArrowRight />}
                onClick={() => setPageNumber(pageNumber + 1)}
              />
            </div>
          </div>
        </Modal>
      );
    case "Diff":
      return (
        <Modal extraWide={true} isOpen={isOpen} onClose={closeModal}>
          <h1 className="font-bold">
            Diff: {versionToDate(modalState.version).localeString} (Revision{" "}
            {modalState.version.id})
          </h1>
          <StaticDiffPolicy
            className="mt-4 md:container min-w-fit overflow-auto max-h-[50vh]"
            oldPolicy={currentPolicy}
            newPolicy={modalState.policy_to_diff}
          />
          <div className="mt-4 space-x-4">
            <Button
              medium
              theme="secondary"
              icon={<HiOutlineX />}
              onClick={() => setModalState(DEFAULT_MODAL_STATE)}
            >
              Back
            </Button>
            <Button
              medium
              theme="primary"
              icon={<HiOutlineCheck />}
              onClick={() =>
                setModalState({
                  mode: "ConfirmRestore",
                  version: modalState.version,
                  policy_to_restore: modalState.policy_to_diff,
                })
              }
            >
              Restore
            </Button>
          </div>
        </Modal>
      );
    case "ConfirmRestore":
      return (
        <Modal extraWide={true} isOpen={isOpen} onClose={closeModal}>
          <h1 className="font-bold">
            Overwrite current policy with version {modalState.version.id}?
          </h1>
          <div className="my-4 overflow-auto max-h-[50vh]">
            <Policy policy={modalState.policy_to_restore} />
          </div>
          <div className="space-x-4">
            <Button
              medium
              theme="secondary"
              icon={<HiOutlineX />}
              onClick={() => setModalState(DEFAULT_MODAL_STATE)}
            >
              Back
            </Button>
            <Button
              medium
              theme="secondary"
              icon={<AiOutlineDiff />}
              onClick={() =>
                setModalState({
                  mode: "Diff",
                  version: modalState.version,
                  policy_to_diff: modalState.policy_to_restore,
                })
              }
            >
              Diff
            </Button>
            <Button
              medium
              theme="primary"
              icon={<HiOutlineCheck />}
              onClick={async () => {
                // set editor value to old policy and save old policy (as new version)
                // close modal
                // reset modal to "list"
                await setPolicy(modalState.policy_to_restore);
                refreshStats();
                onClose();
                setModalState(DEFAULT_MODAL_STATE);
              }}
            >
              Restore
            </Button>
          </div>
        </Modal>
      );
  }
};

const Toast: FunctionComponent<{
  children: ReactNode;
}> = ({ children }) => {
  const { stage, shouldMount } = useTransition(!!children, 150);
  const [lastNonEmptyChildren, setLastNonEmptyChildren] = useState(children);
  useEffect(() => {
    if (children) setLastNonEmptyChildren(children);
  }, [children]);
  return shouldMount ? (
    <div
      className={`fixed left-1/2 -translate-x-1/2 top-8 px-4 py-2 z-10 bg-white rounded-lg shadow-lg transition ${
        stage === "enter" ? "opacity-1" : "opacity-0 scale-95 -translate-y-2"
      }`}
    >
      {lastNonEmptyChildren}
    </div>
  ) : null;
};

/**
 * In theory, anything that could show up here should show up in the editor anyway.
 * This is just a safety measure in case of any discrepancies between the LSP
 * and the API.
 */
const PolicySaveError: FunctionComponent<{
  error: PolicySaveError;
}> = ({ error }) => {
  switch (error.error_type) {
    case "Generic":
      return <span className="text-red-500">{error.message}</span>;
    case "TestsFailed":
      return (
        <>
          <div className="mb-3">Tests didn{"'"}t pass:</div>
          {error.test_results.errors.map(({ message }, i) => (
            <div
              key={i}
              className="bg-gray-100 p-2 text-red-500 font-mono whitespace-pre-wrap"
            >
              {message}
            </div>
          ))}
        </>
      );
    case "Validation":
      return (
        <>
          <div className="mb-3">Policy failed validation:</div>
          {error.errors.map(({ message }, i) => (
            <div
              key={i}
              className="bg-gray-100 p-2 text-red-500 font-mono whitespace-pre-wrap"
            >
              {message}
            </div>
          ))}
        </>
      );
  }
};

const PolicySaveBar: FunctionComponent<{
  emitter: EventTarget;
  savePolicy: () => void;
  runTests: () => Promise<void>;
  showHistory: () => void;
  otherToolbarButtons?: ReactNode;
}> = ({ emitter, savePolicy, runTests, showHistory, otherToolbarButtons }) => {
  const [savingState, setSavingState] = useState<SavingState>(
    SavingState.Default
  );
  const [disabled, setDisabled] = useState(false);
  const updatePolicyAction = useEnvironmentAction("update_policy");
  const [policySaveError, setPolicySaveError] =
    useState<PolicySaveError | null>(null);
  const [showPolicySaveErrorModal, setShowPolicySaveErrorModal] =
    useState(false);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const disable = () => setDisabled(true);
    const enable = () => setDisabled(false);
    const saving = () => {
      setSavingState(SavingState.Saving);
    };
    const runningTests = () => {
      setSavingState(SavingState.RunningTests);
    };
    const resetStateAfterDelay = () => {
      if (timeout) {
        // cancel the last timer
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => setSavingState(SavingState.Default), 5000);
    };

    const savingSuccess = () => {
      setSavingState(SavingState.SavingSuccess);
      resetStateAfterDelay();
    };
    const savingSuccessExisting = () => {
      setSavingState(SavingState.SavingSuccessExisting);
      resetStateAfterDelay();
    };
    const savingFailure = (evt: Event) => {
      setSavingState(SavingState.SavingFailure);
      setPolicySaveError(
        evt instanceof PolicySaveErrorEvent ? evt.error : null
      );
      resetStateAfterDelay();
    };
    const savingFailureUnexpected = () => {
      setSavingState(SavingState.SavingFailureUnexpected);
      resetStateAfterDelay();
    };
    const savingFailureForbidden = () => {
      setSavingState(SavingState.SavingFailureForbidden);
      resetStateAfterDelay();
    };
    const runningTestsSuccess = () => {
      setSavingState(SavingState.RunningTestsSuccess);
      resetStateAfterDelay();
    };
    const runningTestsFailure = () => {
      setSavingState(SavingState.RunningTestsFailure);
      resetStateAfterDelay();
    };

    emitter.addEventListener("disable", disable);
    emitter.addEventListener("enable", enable);
    emitter.addEventListener("saving", saving);
    emitter.addEventListener("runningTests", runningTests);
    emitter.addEventListener("savingSuccess", savingSuccess);
    emitter.addEventListener("savingSuccessExisting", savingSuccessExisting);
    emitter.addEventListener("savingFailure", savingFailure);
    emitter.addEventListener(
      "savingFailureUnexpected",
      savingFailureUnexpected
    );
    emitter.addEventListener("savingFailureForbidden", savingFailureForbidden);
    emitter.addEventListener("runningTestsSuccess", runningTestsSuccess);
    emitter.addEventListener("runningTestsFailure", runningTestsFailure);

    return () => {
      emitter.removeEventListener("disable", disable);
      emitter.removeEventListener("enable", enable);
      emitter.removeEventListener("saving", saving);
      emitter.removeEventListener("runningTests", runningTests);
      emitter.removeEventListener("savingSuccess", savingSuccess);
      emitter.removeEventListener("savingFailure", savingFailure);
      emitter.removeEventListener(
        "savingFailureUnexpected",
        savingFailureUnexpected
      );
      emitter.removeEventListener(
        "savingFailureForbidden",
        savingFailureForbidden
      );
      emitter.removeEventListener("runningTestsSuccess", runningTestsSuccess);
      emitter.removeEventListener("runningTestsFailure", runningTestsFailure);
    };
  }, [setSavingState, setDisabled, emitter]);

  let saveTheme: ButtonTheme = "primary";
  let testTheme: ButtonTheme = "default";
  let saveIcon: ReactNode = <AiOutlineSave />;
  let testIcon: ReactNode = <AiOutlineArrowRight />;
  let diagnostic;
  switch (savingState) {
    case SavingState.SavingSuccess:
      saveTheme = "success";
      saveIcon = <HiOutlineCheck />;
      diagnostic = (
        <span className="text-green-500">Policy saved successfully.</span>
      );
      break;
    case SavingState.SavingSuccessExisting:
      saveTheme = "success";
      saveIcon = <HiOutlineExclamation />;
      diagnostic = (
        <span className="text-yellow-600">
          Policy appears to be unchanged. Saved policy not overwritten.
        </span>
      );
      break;
    case SavingState.SavingFailure:
      saveTheme = "error";
      saveIcon = <HiOutlineX />;
      diagnostic = (
        <>
          <span className="text-red-500">
            Failed to deploy policy. Fix underlined errors before saving.
          </span>
          {policySaveError && (
            <Button
              onClick={() => setShowPolicySaveErrorModal(true)}
              className="ml-4"
              small
            >
              See More
            </Button>
          )}
        </>
      );
      break;
    case SavingState.SavingFailureUnexpected:
      saveTheme = "error";
      saveIcon = <HiOutlineX />;
      diagnostic = (
        <span className="text-red-500">
          There was a problem saving the policy. Please try again later.
        </span>
      );
      break;
    case SavingState.SavingFailureForbidden:
      saveTheme = "error";
      saveIcon = <HiOutlineX />;
      diagnostic = (
        <span className="text-red-500">
          You are not authorized to update the policy.
        </span>
      );
      break;
    case SavingState.RunningTestsSuccess:
      testTheme = "success";
      diagnostic = <span className="text-green-500">All tests passed!</span>;
      break;

    case SavingState.RunningTestsFailure:
      testTheme = "error";
      diagnostic = (
        <span className="text-red-500">
          Tests didn{"'"}t pass. See underlined errors for more info.
        </span>
      );
      break;

    default:
      break;
  }

  return (
    <div className="flex space-x-4 items-center">
      <Toast>{diagnostic}</Toast>
      {policySaveError && (
        <Modal
          isOpen={showPolicySaveErrorModal}
          onClose={() => setShowPolicySaveErrorModal(false)}
          showCloseButton
          wide
        >
          <div className="text-3xl font-space mb-3">
            Failed to Deploy Policy
          </div>
          <PolicySaveError error={policySaveError} />
        </Modal>
      )}
      <Button
        medium
        theme={saveTheme}
        icon={saveIcon}
        disabled={disabled || !updatePolicyAction}
        loading={savingState === SavingState.Saving}
        onClick={savePolicy}
      >
        <span>Deploy</span>
      </Button>
      <Button
        medium
        id="run-tests"
        theme={testTheme}
        icon={testIcon}
        onClick={runTests}
        disabled={disabled}
        loading={savingState === SavingState.RunningTests}
      >
        Run Tests
      </Button>
      <Button
        medium
        disabled={!updatePolicyAction}
        onClick={showHistory}
        icon={<AiOutlineHistory />}
      >
        Revert
      </Button>
      {otherToolbarButtons}
    </div>
  );
};

const MonacoEditorReactComp = dynamic(
  () =>
    import("@typefox/monaco-editor-react").then(
      ({ MonacoEditorReactComp }) => MonacoEditorReactComp
    ),
  { ssr: false, loading: () => <LoadingPage /> }
);

const PolicyEditor = ({
  policy: fetchedPolicy,
  stats,
  refreshStats,
  selectedEnv,
  otherToolbarButtons,
  refreshPolicy,
  onUnsavedChangesChange,
}: {
  policy: string;
  stats: Stats;
  refreshStats: () => void;
  selectedEnv: EnvData;
  otherToolbarButtons?: ReactNode;
  refreshPolicy: () => Promise<void>;
  onUnsavedChangesChange: (unsavedChanges: boolean) => void;
}) => {
  const [saveButtonEmitter, _] = useState(() => new EventTarget());
  const [monacoWrapper, setMonacoWrapper] =
    useState<MonacoEditorLanguageClientWrapper>();
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [autosave, setAutosave] = usePolicyAutosave(selectedEnv);
  const [loadingAutosave, setLoadingAutosave] = useState(true);
  const [restoredAutosave, setRestoredAutosave] = useState<null | string>(null);
  const [showPolicyOverwriteWarning, setShowPolicyOverwriteWarning] =
    useState<boolean>(false);
  const [showRestoreModal, setShowRestoreModal] = useState<boolean>(false);
  const updatePolicyAction = useEnvironmentAction("update_policy");
  const track = useSegmentCallbackV2();

  useEffect(() => {
    if (loadingAutosave) {
      setLoadingAutosave(false);
      if (autosave && autosave.policy !== fetchedPolicy) {
        setShowRestoreModal(true);
      }
    }
  }, [loadingAutosave]);
  const policy = restoredAutosave || fetchedPolicy;

  useEffect(() => {
    onUnsavedChangesChange(!!autosave && autosave.policy !== fetchedPolicy);
  }, [autosave?.policy, fetchedPolicy]);

  const dismissHistoryModal = setShowHistoryModal.bind(null, false);

  // TODO(gj): WOWHACK. Context: https://ososecurity.slack.com/archives/C03L7KDBRB8/p1735074186809509?thread_ts=1735071605.240779&cid=C03L7KDBRB8
  const runAllTestsCommand = monacoWrapper
    ?.getLanguageClient()
    ?.initializeResult?.capabilities.executeCommandProvider?.commands?.find(
      (command) => command.endsWith(":polar.runAllTests")
    );

  const savePolicy = useCallback(async () => {
    if (!monacoWrapper) return;
    try {
      saveButtonEmitter.dispatchEvent(new Event("saving"));
      let src = monacoWrapper.getEditor()!.getValue();
      const resp = await fetch(
        `/web/environment/${selectedEnv.pub_id}/api/policy`,
        {
          method: "POST",
          body: JSON.stringify({
            src,
          }),
        }
      );

      track("Policy Editor Save", {
        success: resp.ok,
      }).unifyFakePageHack(!resp.ok);

      if (resp.ok) {
        if (resp.status === 202) {
          saveButtonEmitter.dispatchEvent(new Event("savingSuccessExisting"));
        } else {
          saveButtonEmitter.dispatchEvent(new Event("savingSuccess"));
        }
        setAutosave(null);
        refreshStats();
      } else if (resp.status === 403) {
        saveButtonEmitter.dispatchEvent(new Event("savingFailureForbidden"));
      } else {
        let error_response: PolicySaveError = await resp.json();
        if (error_response.error_type === "TestsFailed" && runAllTestsCommand) {
          monacoWrapper
            .getLanguageClient()!
            .sendRequest("workspace/executeCommand", {
              command: runAllTestsCommand,
            });
        }
        saveButtonEmitter.dispatchEvent(
          new PolicySaveErrorEvent(error_response)
        );
      }
    } catch (e) {
      saveButtonEmitter.dispatchEvent(new Event("savingFailureUnexpected"));
    } finally {
      const position = monacoWrapper.getEditor()!.getPosition();
      await refreshPolicy();
      if (position) monacoWrapper.getEditor()!.setPosition(position);
    }
  }, [monacoWrapper, saveButtonEmitter, selectedEnv]);

  const runTests = useCallback(async () => {
    if (!monacoWrapper || !runAllTestsCommand) return;
    saveButtonEmitter.dispatchEvent(new Event("runningTests"));
    try {
      const result = await monacoWrapper
        .getLanguageClient()!
        .sendRequest("workspace/executeCommand", {
          command: runAllTestsCommand,
        });
      if (result) {
        saveButtonEmitter.dispatchEvent(new Event("runningTestsSuccess"));
      } else {
        saveButtonEmitter.dispatchEvent(new Event("runningTestsFailure"));
      }
    } catch (e) {
      saveButtonEmitter.dispatchEvent(new Event("runningTestsFailure"));
    }
  }, [monacoWrapper, saveButtonEmitter, selectedEnv]);

  const worker = useMemo(() => new Worker("/lsp.js"), []);

  // this would be more natural as a listener on the language client,
  // e.g. monacoWrapper.getLanguageClient().onNotification("textDocument/publishDiagnostics", ...)
  // but the language client was strangely designed so that registering a listener
  // deregisters other listeners. In this case, that would mean monaco not displaying red squiggles.
  useEffect(() => {
    worker.addEventListener("message", ({ data }) => {
      if (data.method === "textDocument/publishDiagnostics") {
        saveButtonEmitter.dispatchEvent(
          new Event(
            data.params.diagnostics.some(
              ({ severity }: { severity: number }) => severity <= 1
            )
              ? "disable"
              : "enable"
          )
        );
      }
    });
  }, [worker]);

  return (
    <div className="flex flex-col h-full">
      <div className="pt-7 px-4 pb-4 shrink-0 flex">
        <h1 className="font-space text-3xl ml-5">
          Rules Editor <span className="text-lg font-sans">Code mode</span>
        </h1>
        <div className="grow" />
        <PolicySaveBar
          emitter={saveButtonEmitter}
          savePolicy={() => {
            setShowPolicyOverwriteWarning(true);
          }}
          runTests={runTests}
          showHistory={() => setShowHistoryModal(true)}
          otherToolbarButtons={otherToolbarButtons}
        />
      </div>

      <Modal
        isOpen={showPolicyOverwriteWarning}
        onClose={() => setShowPolicyOverwriteWarning(false)}
        extraWide
        tall
      >
        <div className="">
          <div className="text-3xl font-space">Overwrite Policy?</div>
          <div className="mt-3" />
          Deploying this policy will overwrite your existing policy.
          <div className="mt-3" />
          {fetchedPolicy && (
            <details open className="group">
              <summary className="border rounded p-4 group-open:rounded-b-none">
                Summary of changes
              </summary>
              <div className="max-h-[65vh] overflow-auto border rounded border-t-0 rounded-t-none">
                <StaticDiffPolicy
                  newPolicy={autosave?.policy || policy}
                  oldPolicy={fetchedPolicy}
                />
              </div>
            </details>
          )}
          <div className="flex p-6 pb-0 justify-around">
            <Button
              medium
              icon={<AiOutlineClose />}
              onClick={() => setShowPolicyOverwriteWarning(false)}
            >
              Cancel
            </Button>
            <Button
              medium
              theme="primary"
              icon={<AiOutlineSave />}
              disabled={!updatePolicyAction}
              loaderOnClick
              onClick={() => {
                savePolicy().finally(() => {
                  setShowPolicyOverwriteWarning(false);
                });
              }}
            >
              Deploy Policy
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        tall
        wide
        isOpen={showRestoreModal}
        onClose={() => setShowRestoreModal(false)}
      >
        {autosave && (
          <>
            <div className="text-3xl font-space">
              Restore undeployed changes?
            </div>
            <div className="mt-4" />
            <div>You left the rules editor without deploying your changes.</div>
            <div className="mt-2" />
            <div>
              You can resume editing these changes, or you can discard them.
            </div>
            <div className="mt-2" />
            <details open className="group">
              <summary className="border rounded p-4 group-open:rounded-b-none">
                Summary of your undeployed changes
              </summary>
              <div className="max-h-[65vh] overflow-auto border rounded border-t-0 rounded-t-none">
                <StaticDiffPolicy
                  newPolicy={autosave.policy}
                  oldPolicy={fetchedPolicy}
                />
              </div>
            </details>
            <div className="mt-4" />
            <div className="flex justify-around">
              <Button
                medium
                onClick={() => {
                  setRestoredAutosave(null);
                  setAutosave(null);
                  setShowRestoreModal(false);
                }}
              >
                Discard these changes
              </Button>
              <Button
                medium
                onClick={() => {
                  setRestoredAutosave(autosave.policy);
                  setShowRestoreModal(false);
                }}
              >
                Keep editing these changes
              </Button>
            </div>
          </>
        )}
      </Modal>

      <PolicyHistoryModal
        isOpen={showHistoryModal}
        onClose={dismissHistoryModal}
        stats={stats}
        refreshStats={refreshStats}
        selectedEnv={selectedEnv}
        currentPolicy={policy}
        setPolicy={async (policy) => {
          monacoWrapper?.getEditor()?.setValue(policy);
          await savePolicy();
        }}
      />
      <MonacoEditorReactComp
        className="grow"
        userConfig={{
          wrapperConfig: {
            editorAppConfig: {
              $type: "extended",
              useDiffEditor: false,
              codeResources: {
                main: {
                  text: policy,
                  // DO NOT CHANGE THIS FROM "tmp:/".
                  // For unknown reasons, file-schemed uris are
                  // fully busted on Windows with this library
                  // and some peculiarity of our next.js build.
                  // See also https://github.com/CodinGame/monaco-vscode-api/issues/164
                  uri: "tmp:/workspace/policy.polar",
                },
              },
              editorOptions: {
                minimap: { enabled: false },
                automaticLayout: true,
                scrollBeyondLastLine: false,
                glyphMargin: true,
              },
              extensions: [
                {
                  config: {
                    engines: {
                      vscode: "*",
                    },
                    categories: ["Programming Languages"],
                    contributes: {
                      languages: [
                        {
                          id: "polar",
                          extensions: [".polar"],
                          configuration: "./config.json",
                        },
                      ],
                      grammars: [
                        {
                          language: "polar",
                          scopeName: "source.polar",
                          path: "./syntax.json",
                        },
                      ],
                    },
                  },
                  filesOrContents: new Map([
                    ["/config.json", JSON.stringify(extensionConfiguration)],
                    ["/syntax.json", JSON.stringify(tmLanguageSyntax)],
                  ]),
                },
              ],
            },
          },
          languageClientConfig: {
            languageId: "polar",
            options: {
              $type: "WorkerDirect",
              worker,
            },
          },
        }}
        onLoad={(monacoWrapper) => {
          setMonacoWrapper(monacoWrapper);
          import("monaco-editor").then(({ KeyMod, KeyCode }) => {
            monacoWrapper
              .getEditor()!
              .addCommand(KeyMod.CtrlCmd | KeyCode.KeyS, () =>
                setShowPolicyOverwriteWarning(true)
              );
          });
          monacoWrapper.getEditor()!.focus();
        }}
        onTextChanged={(change) => {
          if (
            !loadingAutosave &&
            !showRestoreModal &&
            autosave?.policy != change.main
          ) {
            setAutosave(change.main);
          }
        }}
      />
    </div>
  );
};

const PolicyEditorLoader: React.FC<{
  otherToolbarButtons?: ReactNode;
  onUnsavedChangesChange: (unsavedChanges: boolean) => void;
}> = ({ otherToolbarButtons, onUnsavedChangesChange }) => {
  const [policy, setPolicy] = useState("");
  const [isPolicyLoading, setIsPolicyLoading] = useState(true);
  const { selectedEnv } = useEnv();
  const { data: stats, refresh: refreshStats } = useData<Stats>(
    () => selectedEnv && `/web/environment/${selectedEnv.pub_id}/api/stats`
  );

  const fetchPolicy = async () => {
    if (!selectedEnv) return;
    try {
      const resp = await fetch(
        `/web/environment/${selectedEnv.pub_id}/api/policy`
      );
      const policy = (await resp.json()).policy;
      setPolicy(policy?.src ?? "");
      setIsPolicyLoading(false);
    } catch (e) {
      const msg = `Error while fetching policy: ${e}`;
      console.error(msg);
    }
  };

  useEffect(() => {
    fetchPolicy();
  }, [selectedEnv]);

  if (isPolicyLoading || !stats || !selectedEnv) {
    return <LoadingPage />;
  } else {
    return (
      <PolicyEditor
        otherToolbarButtons={otherToolbarButtons}
        policy={policy}
        stats={stats}
        refreshStats={refreshStats}
        selectedEnv={selectedEnv}
        refreshPolicy={fetchPolicy}
        onUnsavedChangesChange={onUnsavedChangesChange}
      />
    );
  }
};

export default PolicyEditorLoader;
